import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { keys as clustersKeys, useAddToClusterUsingUrlMutation } from '~/queries/clusters'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface FormValues {
  storeUrl: string
}

const validationSchema = Yup.object().shape({
  storeUrl: Yup.string().url('Store URL must be valid').required(FORM_ERRORS.REQUIRED),
})

interface Props {
  clusterId: string
  defaultApps?: string[]
  isOpen: boolean
  onClose: VoidFunction
  onSuccess: VoidFunction
}

const AddAppToClusterModal = ({ clusterId, isOpen, onClose, onSuccess }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: addToCluster } = useAddToClusterUsingUrlMutation()

  /** Handlers */

  const handleFormSubmit = async ({ storeUrl }: FormValues) => {
    try {
      await addToCluster({ clusterId, storeUrl })
      await queryClient.invalidateQueries({ queryKey: clustersKeys.details(clusterId) })
      await queryClient.invalidateQueries({ queryKey: clustersKeys.listAppsForClusters({ clusterId }) })
      reset()
      displayToast('App successfully added to the cluster', 'success')
      onSuccess()
    } catch (e) {
      captureException(e)
      displayToast('App could not be added to the cluster', 'error')
    }
  }

  const handleModalClose = () => {
    reset()
    onClose()
  }

  /** Render */

  return (
    <Modal className="add-app-to-cluster-modal" isOpen={isOpen} onCloseModal={handleModalClose}>
      <ModalHeader>Add app to cluster</ModalHeader>
      <form className="add-app-to-cluster-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody>
          <TextInput
            {...register('storeUrl')}
            className="add-app-to-cluster-form__store-url-input"
            disabled={isSubmitting}
            error={errors.storeUrl?.message}
            label="Store URL"
            placeholder='Paste a store URL and press "Enter"'
          />
        </ModalBody>
        <ModalFooter>
          <Button ghost onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            Create
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default AddAppToClusterModal
